import React, { useRef, useState } from "react";
import { Upload, FileArchive, FileText, Loader2, AlertTriangle, X } from "lucide-react";
import { useFileUpload } from "./useFileUpload";

function fmtBytes(n) {
    if (!n || n <= 0) return "0 B";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function fmtSpeed(bps) {
    if (!bps || bps <= 0) return "—";
    if (bps < 1024 * 1024) return `${(bps / 1024).toFixed(0)} KB/s`;
    return `${(bps / 1024 / 1024).toFixed(2)} MB/s`;
}
function fmtEta(sec) {
    if (!sec || sec <= 0 || !isFinite(sec)) return "—";
    if (sec < 60) return `${Math.ceil(sec)}s`;
    const m = Math.floor(sec / 60);
    return `${m}m ${Math.ceil(sec - m * 60)}s`;
}

function Dropzone({ file, accept, Icon, label, testid, onPick }) {
    const inputRef = useRef(null);
    return (
        <button type="button" data-testid={testid} onClick={() => inputRef.current?.click()}
            className="w-full border-2 border-dashed border-slate-300 hover:border-cb-accent transition-colors p-8 text-left group">
            <Icon className="text-cb-accent mb-3" size={28} strokeWidth={1.5} />
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</div>
            <div className="mt-1 text-cb-dark font-medium truncate">{file ? file.name : "Clicca per selezionare"}</div>
            {file && <div className="mt-1 text-xs text-slate-500">{(file.size / 1024).toFixed(0)} KB</div>}
            <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={onPick} />
        </button>
    );
}

function UploadProgress({ progress, onCancel }) {
    return (
        <div className="border border-slate-200 bg-white p-5" data-testid="upload-progress">
            <div className="flex items-center justify-between text-sm mb-2">
                <span className="font-medium text-cb-dark flex items-center gap-2">
                    <Upload size={14} className="text-cb-accent" /> Caricamento file al server...
                </span>
                <span className="font-mono text-cb-accent font-semibold" data-testid="upload-pct">{progress.pct.toFixed(0)}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded overflow-hidden">
                <div className="h-full bg-cb-accent transition-[width] duration-100 ease-out" style={{ width: `${progress.pct}%` }} data-testid="upload-progress-bar" />
            </div>
            <div className="flex flex-wrap items-center justify-between mt-2 text-xs text-slate-500 gap-2">
                <span data-testid="upload-progress-bytes">{fmtBytes(progress.loaded)} / {fmtBytes(progress.total)}</span>
                <span className="flex items-center gap-3">
                    <span data-testid="upload-progress-speed">⚡ {fmtSpeed(progress.bps)}</span>
                    <span data-testid="upload-progress-eta">⏱ {fmtEta(progress.eta)}</span>
                </span>
            </div>
            <div className="mt-3 flex justify-end">
                <button type="button" data-testid="cancel-upload-btn" onClick={onCancel}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 border border-red-200 hover:border-red-400 bg-red-50 hover:bg-red-100 px-3 py-1.5 transition-colors">
                    <X size={12} /> Annulla caricamento
                </button>
            </div>
        </div>
    );
}

export function Uploader({ passcode, onResult }) {
    const [dxfFile, setDxfFile] = useState(null);
    const [pdfFile, setPdfFile] = useState(null);
    const [debug, setDebug] = useState(false);
    const { busy, phase, progress, err, setErr, run, cancel } = useFileUpload(passcode);

    const pick = (setter) => (e) => { setter(e.target.files?.[0] || null); setErr(""); };

    return (
        <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
                <Dropzone file={dxfFile} accept=".dxf" Icon={FileArchive} label="File DXF (canali in scala)" testid="upload-dxf" onPick={pick(setDxfFile)} />
                <Dropzone file={pdfFile} accept=".pdf,application/pdf" Icon={FileText} label="File PDF (quote e sezioni)" testid="upload-pdf" onPick={pick(setPdfFile)} />
            </div>

            {phase === "upload" && progress && <UploadProgress progress={progress} onCancel={cancel} />}

            {phase === "analyze" && (
                <div className="border border-slate-200 bg-white p-5 flex items-center gap-3" data-testid="analyze-progress">
                    <Loader2 className="text-cb-accent animate-spin" size={18} />
                    <div>
                        <div className="text-sm font-medium text-cb-dark">Analisi in corso sul server</div>
                        <div className="text-xs text-slate-500">Accoppiamento contorni, spline dei flessibili e matching con le quote del PDF...</div>
                    </div>
                </div>
            )}

            {err && <div className="flex items-center gap-2 text-sm text-red-600" data-testid="upload-error"><AlertTriangle size={16} /> {err}</div>}
            <div className="flex flex-wrap items-center gap-3">
                <button data-testid="analyze-btn" onClick={() => run({ dxfFile, pdfFile, debug, onResult })} disabled={busy || !dxfFile || !pdfFile}
                    className="cb-glow inline-flex items-center gap-2 bg-cb-dark hover:bg-cb-slate text-white px-8 py-3.5 font-medium disabled:opacity-50">
                    {busy ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                    {busy ? (phase === "upload" ? "Caricamento in corso..." : "Analisi in corso...") : "Avvia analisi"}
                </button>
                <div className="text-xs text-slate-500">DXF esportato da AutoCAD · PDF con quote di sezione. Max 80 MB per file.</div>
                <label className="ml-auto inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none" data-testid="debug-toggle-label">
                    <input type="checkbox" checked={debug} onChange={(e) => setDebug(e.target.checked)} disabled={busy} data-testid="debug-toggle" className="accent-cb-accent" />
                    Modalità debug (traccia ogni passaggio)
                </label>
            </div>
        </div>
    );
}
